// src/odoo/customers/api/documentsApi.ts
// All comments are in English.

import { http } from "../../../core/http";
import type { DateRangeQuery } from "./overviewApi";

// Base path for company-level customer endpoints
const BASE_COMPANY = "/customers/company";

// ---------- Documents (Sales orders + invoices) ----------

export interface SalesOrderDocument {
  order_id: number;
  order_name: string;
  order_date: string | null;
  order_date_due: string | null;
  amount_total: number;
  currency: string;
  state: string;
  is_overdue: boolean;
  overdue_days: number | null;
}

export interface InvoiceDocument {
  invoice_id: number;
  invoice_name: string;
  invoice_date: string | null;

  // Raw due date from Odoo (preferred field to use in UI)
  invoice_date_due: string | null;

  // Optional alias, kept for backward compatibility
  due_date?: string | null;

  state: string;

  // Payment state from Odoo, kept for UI hints if needed
  payment_state?: string | null;

  // Open balance (what is still outstanding)
  residual: number;

  // Original invoice total
  amount_total: number;

  currency: string;

  // Overdue flags computed in backend based on residual + due date
  is_overdue: boolean;
  overdue_days: number | null;
}

export interface DocumentsQueryParams extends DateRangeQuery {
  limit_sales_orders?: number;
  limit_invoices?: number;
}

export interface DocumentsResponse {
  sales_orders: SalesOrderDocument[];
  invoices: InvoiceDocument[];
}

/**
 * Recent sales orders and invoices for a company-level customer.
 * This is a natural data source for a dedicated "Sales & Invoices" tab.
 */
export async function fetchDocuments(
  companyPartnerId: number,
  params?: DocumentsQueryParams
): Promise<DocumentsResponse> {
  const res = await http.get<DocumentsResponse>(
    `${BASE_COMPANY}/${companyPartnerId}/documents`,
    { params }
  );
  return res.data;
}
